import { EventName } from "module/analytics/types"
import { AppNetwork } from "module/network/container/AppNetwork"
import { formatNumberPositionSize, formatNumberUSD } from "util/format"
import { formatTimeStamp } from "util/time"

import { Box, HStack, Spacer, Table, Td, Text, Th, Thead, Tr, useColorModeValue } from "@chakra-ui/react"
import { Card } from "component/Card"
import { MotionTbody, MotionTr } from "component/Motion"
import { PerpLink } from "component/PerpLink"
import React from "react"
import { useTranslation } from "react-i18next"
import { useMarketTradeHistory } from "sdk-react/history/useMarketTradeHistory"
import { scrollbarStyle } from "theme/scrollbarStyle"

const LIST_LENGTH_DEFAULT = 20

interface MarketTradeListProps {
    tickerSymbol: string
    listLength?: number
}

export const MarketTradeList = React.memo(
    ({ tickerSymbol, listLength = LIST_LENGTH_DEFAULT }: MarketTradeListProps) => {
        const { t } = useTranslation()
        const { tradeHistory } = useMarketTradeHistory({ tickerSymbol: tickerSymbol, length: listLength })
        const { explorerBuilder } = AppNetwork.useContainer()

        const longColor = useColorModeValue("green.500", "green.300")
        const shortColor = useColorModeValue("red.500", "red.400")
        const subText = useColorModeValue("gray.500", "gray.400")

        const container = {
            hidden: { opacity: 0 },
            show: {
                opacity: 1,
                transition: {
                    staggerDirection: -1,
                    when: "beforeChildren",
                    staggerChildren: 0.12,
                },
            },
        }

        return (
            <Card px={0} pt={5} pb={0} w="100%" h="100%">
                <HStack userSelect={"none"} p={4} pt={1}>
                    <Text fontSize="sm" fontWeight="semibold">
                        Market Trades
                    </Text>
                    <Spacer />
                    <Text variant="label" color={subText}>
                        24H
                    </Text>
                </HStack>
                <Box
                    position="relative"
                    w="100%"
                    h="100%"
                    overflowY="auto"
                    minH="220px"
                    // maxH={{ base: "240px", md: "450px", lg: "240px", xl: "240px" }}
                    sx={scrollbarStyle}
                >
                    <Box whiteSpace="nowrap" position="absolute" top={0} left={0} right={0} bottom={0}>
                        <Table mt={3} size="sm" variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>{t("position.size")}</Th>
                                    <Th>{t("general.time")}</Th>
                                    <Th isNumeric>{t("general.price")}</Th>
                                </Tr>
                            </Thead>

                            {tradeHistory.length !== 0 && (
                                <MotionTbody variants={container} initial="hidden" animate="show">
                                    {tradeHistory.map(
                                        ({ txId, exchangedPositionSize, timestamp, swappedPrice }, index) => {
                                            const item = {
                                                hidden: {
                                                    scaleY: 0,
                                                    y: -10,
                                                    // opacity: 0,
                                                    background: "rgba(255,255,255,0)",
                                                },
                                                show: {
                                                    opacity: 1,
                                                    scaleY: 1,
                                                    y: 0,
                                                    background: exchangedPositionSize.gt(0)
                                                        ? ["rgba(56,161,105,0.7)", "rgba(255,255,255,0)"]
                                                        : ["rgba(229,62,62,0.7)", "rgba(255,255,255,0)"],
                                                },
                                            }

                                            return (
                                                <MotionTr overflow="hidden" key={`${txId}-${index}`} variants={item}>
                                                    <Td>
                                                        <Text
                                                            color={exchangedPositionSize.gt(0) ? longColor : shortColor}
                                                        >
                                                            {formatNumberPositionSize(exchangedPositionSize.abs())}
                                                        </Text>
                                                    </Td>
                                                    <Td color="whiteAlpha.500">
                                                        <PerpLink
                                                            trackingProps={{
                                                                eventName: EventName.LINK_TRADE_LIST_CLICK,
                                                            }}
                                                            href={explorerBuilder(txId, "transaction")}
                                                        >
                                                            {formatTimeStamp(timestamp, "M/d T")}
                                                        </PerpLink>
                                                    </Td>
                                                    <Td isNumeric>{formatNumberUSD({ num: swappedPrice })}</Td>
                                                </MotionTr>
                                            )
                                        },
                                    )}
                                </MotionTbody>
                            )}
                        </Table>
                    </Box>
                </Box>
            </Card>
        )
    },
)
